import React, { useState, useEffect } from 'react';
import { Trash2, RefreshCw, AlertCircle, Package, Users, Ship, XCircle, CheckCircle } from 'lucide-react';
import apiService from '../services/api';

function Papelera() {
  const [elementos, setElementos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');

  useEffect(() => {
    fetchPapelera();
    fetchResumen();
  }, [filtroTipo]);

  const fetchPapelera = async () => {
    try {
      setLoading(true);
      const endpoint = filtroTipo
        ? `/papelera?tipo=${filtroTipo}`
        : '/papelera';

      const data = await apiService.get(endpoint);
      setElementos(data.elementos || []);
    } catch (error) {
      console.error('❌ Error al cargar papelera:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResumen = async () => {
    try {
      const data = await apiService.get('/papelera/resumen');
      setResumen(data);
    } catch (error) {
      console.error('❌ Error al cargar resumen:', error);
    }
  };

  const handleRestaurar = async (id) => {
    try {
      await apiService.put(`/papelera/${id}/restaurar`, {});
      alert('✅ Elemento restaurado exitosamente');
      fetchPapelera();
      fetchResumen();
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        alert(`❌ ${error.message || 'Error al conectar con el servidor'}`);
      }
    }
  };

  const handleEliminarPermanente = async (id) => {
    if (!window.confirm('⚠️ ¿Eliminar permanentemente? Esta acción NO se puede deshacer.')) {
      return;
    }

    try {
      await apiService.delete(`/papelera/${id}/permanente`);
      alert('🗑️ Elemento eliminado permanentemente');
      fetchPapelera();
      fetchResumen();
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        alert('❌ Error al eliminar');
      }
    }
  };

  const handleVaciarPapelera = async () => {
    if (!window.confirm('⚠️ ¿Vaciar TODA la papelera? Se eliminarán permanentemente todos los elementos.')) {
      return;
    }

    try {
      const data = await apiService.delete('/papelera/vaciar');
      alert(`✅ Papelera vaciada: ${data.eliminados} elementos eliminados`);
      fetchPapelera();
      fetchResumen();
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        alert('❌ Error al vaciar papelera');
      }
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'registro':
        return <Package size={20} className="text-blue-600" />;
      case 'cliente':
        return <Users size={20} className="text-green-600" />;
      case 'naviera':
        return <Ship size={20} className="text-purple-600" />;
      default:
        return <Trash2 size={20} className="text-gray-600" />;
    }
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      registro: 'Contenedor',
      cliente: 'Cliente',
      naviera: 'Naviera',
      tarifa: 'Tarifa'
    };
    return labels[tipo] || tipo;
  };

  const renderElementoDetalle = (elemento) => {
    const datos = elemento.datosOriginales;

    switch (elemento.tipo) {
      case 'registro':
        return (
          <div className="text-sm text-ink-700">
            <p><strong>Folio:</strong> {datos.folio || 'N/A'}</p>
            <p><strong>Contenedor:</strong> {datos.contenedor || 'N/A'}</p>
            <p><strong>Cliente:</strong> {datos.cliente || 'N/A'}</p>
            <p><strong>Naviera:</strong> {datos.naviera || 'N/A'}</p>
          </div>
        );
      case 'cliente':
        return (
          <div className="text-sm text-ink-700">
            <p><strong>Nombre:</strong> {datos.nombre}</p>
          </div>
        );
      case 'naviera':
        return (
          <div className="text-sm text-ink-700">
            <p><strong>Nombre:</strong> {datos.nombre}</p>
          </div>
        );
      default:
        return <p className="text-sm text-ink-500">Sin detalles disponibles</p>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-ink-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-ink-900"></div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-ink-50 min-h-screen">
      {/* Header */}
      <div className="mb-8 animate-fade-in">
        <h1 className="font-display text-3xl text-ink-900 mb-1 flex items-center gap-3">
          <Trash2 size={32} className="text-ink-700" />
          Papelera de Reciclaje
        </h1>
        <p className="text-ink-400">Elementos eliminados que pueden ser restaurados</p>
      </div>

      {/* Resumen */}
      {resumen && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 animate-fade-in">
          <div className="bg-white border border-ink-200 shadow-soft rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-ink-500 text-sm font-medium">Total Elementos</p>
                <p className="text-3xl font-bold text-ink-900 mt-1">{resumen.totalGeneral}</p>
              </div>
              <Trash2 size={40} className="text-ink-300" />
            </div>
          </div>

          {resumen.resumen && resumen.resumen.map((item) => (
            <div key={item.tipo} className="bg-white rounded-lg p-6 shadow-soft border border-ink-200">
              <div className="flex items-center gap-3 mb-2">
                {getTipoIcon(item.tipo)}
                <p className="text-ink-700 font-semibold">{getTipoLabel(item.tipo)}s</p>
              </div>
              <p className="text-2xl font-bold text-ink-900">{item.total}</p>
              <p className="text-xs text-ink-500 mt-1">Más antiguo: {item.masAntiguo}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filtros y Acciones */}
      <div className="bg-white rounded-lg shadow-soft border border-ink-200 p-6 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => setFiltroTipo('')}
              className={`px-4 py-2 rounded-lg transition ${
                filtroTipo === '' ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setFiltroTipo('registro')}
              className={`px-4 py-2 rounded-lg transition ${
                filtroTipo === 'registro' ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
              }`}
            >
              Contenedores
            </button>
            <button
              onClick={() => setFiltroTipo('cliente')}
              className={`px-4 py-2 rounded-lg transition ${
                filtroTipo === 'cliente' ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
              }`}
            >
              Clientes
            </button>
            <button
              onClick={() => setFiltroTipo('naviera')}
              className={`px-4 py-2 rounded-lg transition ${
                filtroTipo === 'naviera' ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
              }`}
            >
              Navieras
            </button>
          </div>

          <button
            onClick={handleVaciarPapelera}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={elementos.length === 0}
          >
            <Trash2 size={18} />
            Vaciar Papelera
          </button>
        </div>
      </div>

      {/* Lista de Elementos */}
      <div className="bg-white rounded-lg shadow-soft border border-ink-200">
        {elementos.length === 0 ? (
          <div className="text-center py-16">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <p className="text-xl font-semibold text-ink-900">¡Papelera vacía!</p>
            <p className="text-ink-500 mt-2">No hay elementos eliminados</p>
          </div>
        ) : (
          <div className="divide-y divide-ink-200">
            {elementos.map((elemento) => (
              <div key={elemento.id} className="p-6 hover:bg-ink-50 transition">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-ink-100 rounded-lg">
                      {getTipoIcon(elemento.tipo)}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-ink-100 text-ink-700 border border-ink-200 rounded-full text-xs font-semibold">
                          {getTipoLabel(elemento.tipo)}
                        </span>
                        <span className="text-xs text-ink-500">
                          Eliminado: {new Date(elemento.eliminadoEn).toLocaleString('es-MX')}
                        </span>
                      </div>

                      {renderElementoDetalle(elemento)}

                      {elemento.eliminadoPorNombre && (
                        <p className="text-xs text-ink-500 mt-2">
                          Eliminado por: <strong>{elemento.eliminadoPorNombre}</strong>
                        </p>
                      )}

                      {elemento.motivo && (
                        <p className="text-xs text-ink-500">
                          Motivo: {elemento.motivo}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleRestaurar(elemento.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                      title="Restaurar"
                    >
                      <RefreshCw size={20} />
                    </button>
                    <button
                      onClick={() => handleEliminarPermanente(elemento.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Eliminar permanentemente"
                    >
                      <XCircle size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alert Info */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-yellow-800">
          <p className="font-semibold mb-1">Información importante:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Los elementos pueden ser restaurados en cualquier momento</li>
            <li>La eliminación permanente es irreversible</li>
            <li>Se recomienda vaciar la papelera periódicamente</li>
            <li>Los elementos eliminados permanecen hasta que los elimines permanentemente</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Papelera;
